import { FC, ReactNode, createContext, useContext, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLocalStorage } from './useLocalStorage'
import { UserProfile } from '../models/global.types'
import { useDispatch } from 'react-redux'
import apiServices from '../libs/services'
import { setLoggingOut } from '../libs/api'

interface AuthContextValue {
  user?: any
  userProfile: UserProfile | null
  login: (data: any) => void
  logout: () => void
  setUserProfile: (data: any) => void
  refreshUserProfile: () => Promise<void>
}

const initialValue: AuthContextValue = {
  user: null,
  userProfile: null,
  login: () => {},
  logout: () => {},
  setUserProfile: () => {},
  refreshUserProfile: async () => {}
}

const AuthContext = createContext<AuthContextValue>(initialValue)

interface AuthProviderContext {
  children: ReactNode
}

export const AuthProvider: FC<AuthProviderContext> = ({ children }: AuthProviderContext) => {
  const [user, setUser] = useLocalStorage('user', null)
  const [userProfile, setUserProfile] = useLocalStorage('userProfile', null)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // call this function when you want to authenticate the user
  const login = useCallback(async (data: any) => {
    setUser(data)
  }, [setUser])

  // call this function to sign out logged in user
  const logout = useCallback((): void => {
    setLoggingOut(true)
    setUser(null)
    setUserProfile(null)
    // Clear SSO token from localStorage to ensure complete logout
    localStorage.removeItem('dopageAccessToken')
    dispatch({ type: 'RESET' })
    navigate('/', { replace: true })
    setLoggingOut(false)
  }, [setUser, setUserProfile, dispatch, navigate])

  // call this function to refresh user profile from backend
  const refreshUserProfile = useCallback(async (): Promise<void> => {
    const res = await apiServices.getUserProfile()
    if (res?.data?.data != null) {
      setUserProfile(res.data.data)
    }
  }, [setUserProfile])

  const value = useMemo(
    () => ({
      user,
      userProfile,
      login,
      logout,
      setUserProfile,
      refreshUserProfile
    }),
    [user, userProfile, login, logout, setUserProfile, refreshUserProfile]
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth: () => AuthContextValue = () => {
  return useContext(AuthContext)
}
